import { useState } from "react";
import Logo from '../components/Logo';
import { useNavigate } from "react-router-dom";
import { useCampaign } from "../context/CampaignContext";

const SUB_STEPS = ["Business Info", "Campaign Goal", "Target Audience", "Budget & Guidance"];

const GOALS = [
  { id:"leads",   icon:"🎯", title:"Get Leads",              desc:"People fill a form or contact you" },
  { id:"calls",   icon:"📞", title:"Get Phone Calls",        desc:"Customers call your business directly" },
  { id:"sales",   icon:"🛒", title:"Drive Sales",            desc:"Someone buys a product or service" },
  { id:"traffic", icon:"🌐", title:"Drive Website Traffic",  desc:"Send visitors to your website" },
  { id:"youtube", icon:"▶️", title:"Promote a YouTube Video", desc:"Increase views and engagement" },
];

const AUDIENCE = [
  { id:"searching",  label:"People actively searching for your service", default:true },
  { id:"interested", label:"People interested in your service" },
  { id:"both",       label:"Both" },
];

const LOCATIONS = ["Lagos","Abuja","Dubai","London","New York","Other"];

const navItems = [
  { id:"home",      icon:"🏠", label:"Home",          path:"/dashboard" },
  { id:"build",     icon:"⚙️", label:"Build Campaign", path:"/setup" },
  { id:"guide",     icon:"📋", label:"Launch Guide",   path:"/launch-guide" },
  { id:"templates", icon:"📁", label:"Templates",      path:"/dashboard" },
  { id:"profile",   icon:"👤", label:"Profile",        path:"/dashboard" },
];

export default function Screen3() {
  const navigate = useNavigate();
  const { updateSelections, userSelections } = useCampaign();
  const [sub, setSub]         = useState(0);
  const [activeNav, setActiveNav] = useState("build");

  // Dynamic recommendation based on business type chosen in Screen 1
  const GOAL_RECOMMENDATIONS = {
    realestate: { goal:"Get Leads",            reason:"Real estate businesses get the best results from lead generation — people fill a form to enquire about properties." },
    local:      { goal:"Get Phone Calls",       reason:"Local service businesses convert best when customers can call directly from the ad." },
    ecommerce:  { goal:"Drive Sales",           reason:"E-commerce businesses should focus on driving direct purchases and conversions." },
    coach:      { goal:"Get Leads",            reason:"Coaches and consultants grow fastest by capturing leads who book discovery calls." },
    creator:    { goal:"Drive Website Traffic", reason:"Content creators benefit most from driving traffic to their channel, blog or platform." },
    app:        { goal:"Drive Website Traffic", reason:"App businesses grow fastest by driving traffic to their app store listing or website to get installs and signups." },
    other:      { goal:"Get Leads",            reason:"For most businesses, generating leads is the most measurable and cost-effective goal." },
  };
  const rec = GOAL_RECOMMENDATIONS[userSelections?.businessType] || GOAL_RECOMMENDATIONS.other;

  const [form, setForm] = useState({
    businessName:"", website:"", offering:"", businessLocation:"", targetArea:"",
    goal:"", audience:"searching", safeTargeting:true,
    country:"", state:"", city:"", budget:"recommended", customBudget:"",
  });

  const upd = (k,v) => setForm(f => ({...f, [k]:v}));

  const canNext = () => {
    if (sub===0) return form.businessName.trim() && form.offering.trim() && form.businessLocation.trim();
    if (sub===1) return !!form.goal;
    if (sub===2) return form.country.trim() && form.audience;
    if (sub===3) return !!form.budget;
    return false;
  };

  const BUDGET_REC = { leads:"$10–$20/day", calls:"$10–$15/day", traffic:"$8–$15/day", youtube:"$5–$10/day", sales:"$15–$30/day" };
  const budget = parseFloat(form.customBudget) || 0;
  const minClicks = Math.max(2, Math.floor(budget*0.5));
  const maxClicks = Math.max(5, Math.floor(budget*1.5));

  const budgetWarning = form.budget==="starter" ? "⚠️ A $5–$10/day budget may not generate enough data. Consider $10–$20/day." : null;
  const budgetTip = form.budget==="recommended" ? "✅ Great choice! This is the sweet spot for beginners." : form.budget==="aggressive" ? "🚀 This works well once your campaign is tested first." : null;

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <button style={s.backBtn} onClick={() => navigate(-1)}>← Back</button>
          <Logo size={34} showText={true} textSize={17} />
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <span style={s.screenTag}>Step 2 of 7</span>
          <div style={s.avatar}>AN</div>
        </div>
      </header>

      <div style={s.body}>
        <div style={s.overallRow}>
          <div style={s.barBg}><div style={{ ...s.barFill, width:`${((sub+1)/4)*100}%` }} /></div>
          <span style={s.overallLabel}>Step 2 of 7 — Business Setup</span>
        </div>

        <div style={s.subTrack}>
          {SUB_STEPS.map((label,i) => (
            <div key={i} style={s.subItem}>
              <div style={{ ...s.subDot, background: i<sub ? "#6366f1" : i===sub ? "#6366f1" : "#e2e8f0", boxShadow: i===sub ? "0 0 0 4px rgba(99,102,241,0.15)" : "none" }}>
                {i<sub ? <span style={{color:"#fff",fontSize:10,fontWeight:800}}>✓</span> : <span style={{color: i===sub?"#fff":"#94a3b8",fontSize:10,fontWeight:700}}>{i+1}</span>}
              </div>
              <span style={{ ...s.subLabel, color: i<=sub?"#1e293b":"#94a3b8", fontWeight: i===sub?700:400 }}>{label}</span>
              {i<SUB_STEPS.length-1 && <div style={{ ...s.subLine, background: i<sub?"#6366f1":"#e2e8f0" }} />}
            </div>
          ))}
        </div>

        <div style={s.card}>
          {/* ── 3A Business Info ── */}
          {sub===0 && <>
            <div style={s.badge}>Step 3A</div>
            <h2 style={s.cardTitle}>Tell us about your business</h2>
            <p style={s.cardSub}>We'll use this to build your campaign structure and keywords.</p>
            <Field label="Business Name" req><input style={s.input} placeholder="e.g. EJO Properties" value={form.businessName} onChange={e=>upd("businessName",e.target.value)} /></Field>
            <Field label="Website URL" tag="optional"><input style={s.input} placeholder="https://yourwebsite.com" value={form.website} onChange={e=>upd("website",e.target.value)} /><Helper>💡 If you don't have a website, you can still run ads.</Helper></Field>
            <Field label="What are you promoting?" req><textarea style={s.textarea} rows={3} placeholder="e.g. Luxury apartments for sale in Lagos" value={form.offering} onChange={e=>upd("offering",e.target.value)} /><Helper>Be specific — this drives your keywords and ads.</Helper></Field>
            <Field label="Where is your business located?" req>
              <select style={s.select} value={form.businessLocation} onChange={e=>upd("businessLocation",e.target.value)}>
                <option value="">Select a location…</option>
                {LOCATIONS.map(l=><option key={l} value={l}>{l}</option>)}
              </select>
            </Field>
            <Field label="Where do you want your ads to show?" tag="optional"><input style={s.input} placeholder="e.g. Lagos, Dubai, or all of Nigeria" value={form.targetArea} onChange={e=>upd("targetArea",e.target.value)} /><Helper>You can target a city, country, or multiple locations.</Helper></Field>
          </>}

          {/* ── 3B Campaign Goal ── */}
          {sub===1 && <>
            <div style={s.badge}>Step 3B</div>
            <h2 style={s.cardTitle}>What is your main goal?</h2>
            <p style={s.cardSub}>Choose the one result that matters most right now.</p>
            <div style={s.recBox}>
              <div style={s.recBadge}>⭐ Recommended for You</div>
              <div style={{ fontSize:16,fontWeight:800,color:"#1e293b",fontFamily:"sans-serif",marginBottom:4 }}>{rec.goal}</div>
              <div style={{ fontSize:12,color:"#64748b",fontFamily:"sans-serif" }}>{rec.reason}</div>
            </div>
            <div style={s.goalList}>
              {GOALS.map(g=>(
                <button key={g.id} style={{ ...s.goalCard, borderColor: form.goal===g.id?"#6366f1":"#e2e8f0", background: form.goal===g.id?"linear-gradient(135deg,#eef2ff,#f0f9ff)":"#fafafa", boxShadow: form.goal===g.id?"0 4px 18px rgba(99,102,241,0.15)":"0 1px 4px rgba(0,0,0,0.04)" }} onClick={()=>upd("goal",g.id)}>
                  <div style={{display:"flex",alignItems:"center",gap:14,flex:1}}>
                    <span style={{fontSize:24,flexShrink:0}}>{g.icon}</span>
                    <div><div style={{fontSize:15,fontWeight:700,color: form.goal===g.id?"#4f46e5":"#1e293b",fontFamily:"sans-serif",marginBottom:2}}>{g.title}</div><div style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif"}}>{g.desc}</div></div>
                  </div>
                  <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${form.goal===g.id?"#6366f1":"#cbd5e1"}`,background: form.goal===g.id?"#6366f1":"transparent",flexShrink:0}} />
                </button>
              ))}
            </div>
          </>}

          {/* ── 3C Target Audience ── */}
          {sub===2 && <>
            <div style={s.badge}>Step 3C</div>
            <h2 style={s.cardTitle}>Who do you want to reach?</h2>
            <p style={s.cardSub}>Keep targeting simple and focused for your first campaign.</p>
            <Field label="Target Location" req>
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                <input style={{...s.input,flex:1}} placeholder="Country (e.g. Nigeria)" value={form.country} onChange={e=>upd("country",e.target.value)} />
                <input style={{...s.input,flex:1}} placeholder="City (e.g. Lagos)" value={form.city} onChange={e=>upd("city",e.target.value)} />
              </div>
            </Field>
            <Field label="Who are your ideal customers?">
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                {AUDIENCE.map(a=>(
                  <button key={a.id} style={{...s.audienceBtn,borderColor: form.audience===a.id?"#6366f1":"#e2e8f0",background: form.audience===a.id?"#eef2ff":"#fafafa",color: form.audience===a.id?"#4f46e5":"#334155",fontWeight: form.audience===a.id?700:500}} onClick={()=>upd("audience",a.id)}>
                    <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${form.audience===a.id?"#6366f1":"#cbd5e1"}`,background: form.audience===a.id?"#6366f1":"transparent",flexShrink:0}} />
                    <span style={{flex:1}}>{a.label}</span>
                    {a.default && <span style={{fontSize:9,fontWeight:800,color:"#059669",background:"#dcfce7",padding:"2px 8px",borderRadius:20,fontFamily:"sans-serif"}}>Default</span>}
                  </button>
                ))}
              </div>
              <Helper>For beginners, "People actively searching" gives the best results.</Helper>
            </Field>
            <Field label="Targeting Protection">
              <div style={{...s.toggleCard,borderColor: form.safeTargeting?"#6366f1":"#e2e8f0",background: form.safeTargeting?"#eef2ff":"#fafafa"}} onClick={()=>upd("safeTargeting",!form.safeTargeting)}>
                <div><div style={{fontSize:14,fontWeight:700,color:"#1e293b",fontFamily:"sans-serif"}}>Use Beginner-Safe Targeting</div><div style={{fontSize:12,color:"#64748b",marginTop:3,fontFamily:"sans-serif"}}>We'll warn you before enabling settings that waste budget</div></div>
                <div style={{width:44,height:24,borderRadius:12,background: form.safeTargeting?"#6366f1":"#e2e8f0",position:"relative",flexShrink:0}}>
                  <div style={{width:18,height:18,borderRadius:"50%",background:"#fff",position:"absolute",top:3,left: form.safeTargeting?23:3,transition:"left 0.2s",boxShadow:"0 1px 4px rgba(0,0,0,0.2)"}} />
                </div>
              </div>
              {form.safeTargeting && <div style={{...s.tipBox,marginTop:10}}><span>🛡️</span><span style={s.tipText}>Smart mode is ON. Common mistakes will be flagged before they cost you money.</span></div>}
            </Field>
          </>}

          {/* ── 3D Budget ── */}
          {sub===3 && <>
            <div style={s.badge}>Step 3D</div>
            <h2 style={s.cardTitle}>Set your budget</h2>
            <p style={s.cardSub}>Choose a safe starting daily budget. You can always change it later.</p>
            <div style={s.recBox}>
              <div style={s.recBadge}>💡 Smart Recommendation</div>
              <div style={{fontSize:17,fontWeight:800,color:"#1e293b",fontFamily:"sans-serif",marginBottom:4}}>{BUDGET_REC[form.goal] || "$10–$20/day"}</div>
              <div style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif"}}>This allows your campaign to gather enough data without overspending too quickly.</div>
            </div>
            <Field label="How much do you want to spend daily?" req>
              <div style={{position:"relative"}}>
                <span style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",fontSize:16,color:"#6366f1",fontWeight:800,fontFamily:"sans-serif"}}>$</span>
                <input style={{...s.input,paddingLeft:28,fontSize:20,fontWeight:700,color:"#6366f1"}} placeholder="10" type="number" min="1" value={form.customBudget} onChange={e=>upd("customBudget",e.target.value)} />
                <span style={{position:"absolute",right:14,top:"50%",transform:"translateY(-50%)",fontSize:13,color:"#94a3b8",fontFamily:"sans-serif"}}>/day</span>
              </div>
            </Field>
            {budget>0 && <div style={{...s.tipBox,marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:800,color:"#0369a1",fontFamily:"sans-serif",marginBottom:8}}>📊 Estimated Daily Results</div>
              <div style={{fontSize:24,fontWeight:800,color:"#0369a1",fontFamily:"sans-serif"}}>{minClicks}–{maxClicks} clicks/day</div>
              <div style={{fontSize:11,color:"#64748b",fontFamily:"sans-serif",marginTop:4}}>Estimates only. Actual results vary.</div>
            </div>}
            {budgetWarning && <div style={{...s.tipBox,background:"#fff7ed",border:"1px solid #fed7aa",marginBottom:14}}><span style={s.tipText}>{budgetWarning}</span></div>}
            {budgetTip && <div style={{...s.tipBox,background:"#f0fdf4",border:"1px solid #bbf7d0",marginBottom:14}}><span style={s.tipText}>{budgetTip}</span></div>}
            <div style={{background:"#fff1f2",border:"1px solid #fecdd3",borderRadius:12,padding:"16px 18px"}}>
              <div style={{fontSize:12,fontWeight:800,color:"#be123c",fontFamily:"sans-serif",marginBottom:10}}>⛔ Beginner Mistakes to Avoid</div>
              {["Do NOT select Display Network — it spends your budget on banner ads you cannot control","Do NOT target every country — focus on one city or region to start","Do NOT start with a huge budget — test small first, then scale what works"].map((tip,i)=>(
                <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",marginBottom:6}}>
                  <span style={{color:"#be123c",fontWeight:800,flexShrink:0,fontFamily:"sans-serif"}}>•</span>
                  <span style={{fontSize:13,color:"#7c2d12",fontFamily:"sans-serif",lineHeight:1.5}}>{tip}</span>
                </div>
              ))}
            </div>
          </>}

          {/* Nav buttons */}
          <div style={s.btnRow}>
            {sub>0 && <button style={s.prevBtn} onClick={()=>setSub(p=>p-1)}>← Back</button>}
            <button
              style={{...s.primaryBtn,marginLeft:"auto",opacity: canNext()?1:0.4,cursor: canNext()?"pointer":"not-allowed"}}
              onClick={()=>{ if(!canNext()) return; if(sub<3){ setSub(p=>p+1); } else {
                updateSelections({
                  businessName: form.businessName,
                  offering:     form.offering,
                  location:     form.businessLocation,
                  targetArea:   form.targetArea,
                  goal:         form.goal,
                  country:      form.country,
                  city:         form.city,
                  budget:       form.customBudget || '15',
                  audience:     form.audience,
                });
                navigate("/campaign-type");
              }}}
            >
              {sub===3 ? "Save & Continue →" : "Continue →"}
            </button>
          </div>
        </div>
        <p style={{fontSize:11,color:"#94a3b8",textAlign:"center",marginTop:16,fontFamily:"sans-serif"}}>Your answers personalise your campaign. You can edit them anytime.</p>
        <div style={{height:90}} />
      </div>

      <nav style={s.bottomNav}>
        {navItems.map(n=>{
          const on = activeNav===n.id;
          return <button key={n.id} style={{...s.navItem,color: on?"#6366f1":"#94a3b8"}} onClick={()=>{setActiveNav(n.id);navigate(n.path);}}>
            <span style={{fontSize:20}}>{n.icon}</span>
            <span style={{fontSize:10,fontFamily:"sans-serif",fontWeight: on?700:400,color: on?"#6366f1":"#94a3b8"}}>{n.label}</span>
          </button>;
        })}
      </nav>
    </div>
  );
}

function Field({label,req,tag,children}){return <div style={{marginBottom:18}}><label style={{display:"block",fontSize:13,fontWeight:700,color:"#334155",marginBottom:8,fontFamily:"sans-serif"}}>{label}{req&&<span style={{color:"#ef4444"}}> *</span>}{tag&&<span style={{color:"#94a3b8",fontWeight:400,fontSize:11}}> ({tag})</span>}</label>{children}</div>;}
function Helper({children}){return <p style={{fontSize:12,color:"#64748b",fontFamily:"sans-serif",margin:"6px 0 0",lineHeight:1.5}}>{children}</p>;}

const s = {
  page:       {minHeight:"100vh",background:"linear-gradient(160deg,#f8faff 0%,#eef2ff 60%,#f0fdf4 100%)",fontFamily:"'Georgia',serif",paddingBottom:60},
  header:     {display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 24px",background:"#fff",borderBottom:"1px solid #e2e8f0",boxShadow:"0 1px 8px rgba(0,0,0,0.05)",position:"sticky",top:0,zIndex:200},
  logo:       {display:"flex",alignItems:"center",gap:10},
  mark:       {width:34,height:34,borderRadius:9,background:"linear-gradient(135deg,#6366f1,#818cf8)",color:"#fff",fontWeight:800,fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"},
  logoTxt:    {fontSize:17,fontWeight:800,color:"#1e293b",fontFamily:"sans-serif",letterSpacing:"-0.3px"},
  backBtn:    {background:"none",border:"none",fontSize:13,fontWeight:600,color:"#6366f1",cursor:"pointer",fontFamily:"sans-serif",padding:"6px 8px"},
  screenTag:  {fontSize:12,color:"#6366f1",fontFamily:"sans-serif",fontWeight:700,background:"#eef2ff",padding:"4px 10px",borderRadius:20},
  avatar:     {width:34,height:34,borderRadius:"50%",background:"linear-gradient(135deg,#f59e0b,#fbbf24)",color:"#fff",fontWeight:700,fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"},
  body:       {maxWidth:620,margin:"0 auto",padding:"24px 20px 0"},
  overallRow: {display:"flex",alignItems:"center",gap:12,marginBottom:20},
  barBg:      {flex:1,height:5,borderRadius:99,background:"#e2e8f0",overflow:"hidden"},
  barFill:    {height:"100%",borderRadius:99,background:"linear-gradient(90deg,#6366f1,#818cf8)",transition:"width 0.4s ease"},
  overallLabel:{fontSize:11,color:"#64748b",fontFamily:"sans-serif",whiteSpace:"nowrap",fontWeight:600},
  subTrack:   {display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20,position:"relative"},
  subItem:    {display:"flex",flexDirection:"column",alignItems:"center",flex:1,position:"relative"},
  subDot:     {width:26,height:26,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:5,transition:"all 0.3s",zIndex:1},
  subLabel:   {fontSize:10,fontFamily:"sans-serif",textAlign:"center",lineHeight:1.3},
  subLine:    {position:"absolute",top:12,left:"60%",right:"-40%",height:2,transition:"background 0.3s",zIndex:0},
  card:       {background:"#fff",borderRadius:20,boxShadow:"0 4px 40px rgba(99,102,241,0.08),0 1px 8px rgba(0,0,0,0.05)",padding:"30px 26px",border:"1px solid #f1f5f9"},
  badge:      {display:"inline-block",padding:"4px 12px",background:"#eef2ff",color:"#6366f1",borderRadius:20,fontSize:11,fontWeight:700,fontFamily:"sans-serif",marginBottom:10,letterSpacing:"0.5px"},
  cardTitle:  {fontSize:24,fontWeight:700,color:"#0f172a",margin:"0 0 8px",lineHeight:1.2},
  cardSub:    {fontSize:14,color:"#64748b",margin:"0 0 24px",lineHeight:1.55,fontFamily:"sans-serif"},
  input:      {width:"100%",padding:"12px 14px",border:"1.5px solid #e2e8f0",borderRadius:10,fontSize:14,color:"#1e293b",outline:"none",boxSizing:"border-box",fontFamily:"sans-serif",background:"#fafbff"},
  textarea:   {width:"100%",padding:"12px 14px",border:"1.5px solid #e2e8f0",borderRadius:10,fontSize:14,color:"#1e293b",outline:"none",boxSizing:"border-box",fontFamily:"sans-serif",background:"#fafbff",resize:"vertical",lineHeight:1.5},
  select:     {width:"100%",padding:"12px 14px",border:"1.5px solid #e2e8f0",borderRadius:10,fontSize:14,color:"#1e293b",outline:"none",boxSizing:"border-box",fontFamily:"sans-serif",background:"#fafbff",cursor:"pointer"},
  recBox:     {background:"linear-gradient(135deg,#eef2ff,#f0fdf4)",border:"1.5px solid #c7d2fe",borderRadius:14,padding:"16px 18px",marginBottom:20},
  recBadge:   {fontSize:11,fontWeight:800,color:"#6366f1",fontFamily:"sans-serif",letterSpacing:"0.5px",marginBottom:6},
  goalList:   {display:"flex",flexDirection:"column",gap:12},
  goalCard:   {display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 18px",borderRadius:14,border:"2px solid",cursor:"pointer",transition:"all 0.2s",textAlign:"left"},
  audienceBtn:{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",borderRadius:10,border:"1.5px solid",cursor:"pointer",fontSize:14,fontFamily:"sans-serif",transition:"all 0.2s"},
  toggleCard: {display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",borderRadius:12,border:"1.5px solid",cursor:"pointer",transition:"all 0.2s"},
  tipBox:     {display:"flex",gap:10,padding:"13px 15px",background:"#f0f9ff",borderRadius:10,border:"1px solid #bae6fd",marginBottom:18},
  tipText:    {fontSize:13,color:"#475569",lineHeight:1.55,fontFamily:"sans-serif"},
  btnRow:     {display:"flex",alignItems:"center",gap:12,marginTop:28,paddingTop:22,borderTop:"1px solid #f1f5f9"},
  prevBtn:    {padding:"12px 22px",borderRadius:10,border:"1.5px solid #e2e8f0",background:"#fff",fontSize:14,fontWeight:600,color:"#475569",cursor:"pointer",fontFamily:"sans-serif"},
  primaryBtn: {padding:"13px 26px",borderRadius:10,background:"linear-gradient(135deg,#6366f1,#818cf8)",color:"#fff",border:"none",fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"sans-serif",boxShadow:"0 4px 16px rgba(99,102,241,0.3)"},
  bottomNav:  {position:"fixed",bottom:0,left:0,right:0,background:"#fff",borderTop:"1px solid #e2e8f0",display:"flex",justifyContent:"space-around",padding:"8px 0 12px",boxShadow:"0 -2px 16px rgba(0,0,0,0.06)",zIndex:200},
  navItem:    {display:"flex",flexDirection:"column",alignItems:"center",gap:3,background:"none",border:"none",cursor:"pointer",padding:"4px 12px"},
};